import React from "react";
import { useSelector } from "react-redux";
import { handleLogout } from "../framework/redux/slices/sessionSlice";
import AppBar from "@mui/material/AppBar";
import Badge from "@mui/material/Badge";
import Box, { BoxProps } from "@mui/material/Box";
import Button from "@mui/material/Button";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import EventIcon from "@mui/icons-material/Event";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";
import { useTranslation } from "react-i18next";
import appConfig from "./appConfig";
import { useAppDispatch, useAppSelector } from "../framework/redux/hooks";
import { Assignment, Inventory, Notifications } from "@mui/icons-material";
import BuildIcon from "@mui/icons-material/Build";
import { useAppOptionValueEquals } from "../hooks/framework/useAppOptionValue";
import {
    optionOwnerHasEasyOppTelematicsEnabled,
    optionOwnerHasTransportEnabled,
    optionOwnerHasTransportNotificationsEnabled,
    OptionFormsEnabled,
} from "../models/common/consts";
import { useSelectCurrentWorkShiftTimeSlot } from "../hooks/worktime/useSelectWorkShiftTimeSlots";
import { isServiceVisibleForDriver } from "../components/services/vehicleMaintenanceUtils";

interface INavItemProps {
    label: string;
    path?: string;
    icon?: JSX.Element;
    onClick?: () => void;
    isVisible?: () => boolean;
    disabled?: boolean;
    hasBadgeDot?: boolean;
    badgeContent?: number;
}

const NavItem = ({ path, icon, label, onClick, isVisible, disabled, hasBadgeDot, badgeContent }: INavItemProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const visible = !isVisible || isVisible();

    const handleClick = (path: string, onClick: () => void) => {
        if (!disabled) {
            if (path) {
                navigate(path);
            } else if (onClick) {
                onClick();
            }
        }
    };

    if (!visible) {
        return null;
    }

    return (
        <ListItem disablePadding>
            <ListItemButton
                onClick={() => handleClick(path, onClick)}
                selected={!!(path && matchPath(location.pathname, path))}
                disabled={disabled}
            >
                {icon ? (
                    <ListItemIcon sx={{ color: disabled ? "gray" : "white.main" }}>
                        {hasBadgeDot ? (
                            <Badge
                                variant="dot"
                                color="info"
                                overlap="circular"
                                sx={{
                                    "& .MuiBadge-badge": { bgcolor: "#1976d2" },
                                }}
                            >
                                {icon}
                            </Badge>
                        ) : badgeContent ? (
                            <Badge badgeContent={badgeContent} color="error">
                                {icon}
                            </Badge>
                        ) : (
                            icon
                        )}
                    </ListItemIcon>
                ) : null}
                <ListItemText primary={label} />
            </ListItemButton>
        </ListItem>
    );
};

const HeaderLogo = ({ sx }: BoxProps) => {
    const navigate = useNavigate();
    const hasUnconfirmedNotifications = useSelector(
        (state: any) => state.notification.unconfirmedCount > 0
    );

    const handleLogoClick = () => {
        if (!hasUnconfirmedNotifications) {
            navigate("/");
        }
    };

    return (
        <Box
            sx={{
                backgroundImage: `url(${appConfig.clientUrl}EasyOpp_nega.svg)`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "contain",
                backgroundPosition: "center",
                height: 30,
                flexGrow: 1,
                mb: "2px",
                ...(sx || {}),
                cursor: hasUnconfirmedNotifications ? "not-allowed" : "pointer",
            }}
            onClick={handleLogoClick}
        />
    );
};

export const Header = () => {
    const [menuOpen, setMenuOpen] = React.useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const current = useSelectCurrentWorkShiftTimeSlot();

    const [orderNotificationsEnabled] = useAppOptionValueEquals(
        optionOwnerHasTransportNotificationsEnabled,
        true
    );
    const [transportEnabled] = useAppOptionValueEquals(
        optionOwnerHasTransportEnabled,
        true
    );
    const [telematicsEnabled] = useAppOptionValueEquals(
        optionOwnerHasEasyOppTelematicsEnabled,
        true
    );
    const [formsEnabled] = useAppOptionValueEquals(
        OptionFormsEnabled,
        true
    );
    const canAddAndEditDayBookings = useAppSelector(
        ({ session }) =>
            session.employeeGroupParameters?.canAddAndEditDayBookings
    );

    const dispatch = useAppDispatch();

    const hasUnconfirmedNotifications = useSelector(
        (state: any) => state.notification.unconfirmedCount > 0
    );

    const maintenanceState = useAppSelector((s) => s.vehicleMaintenance);
    const hasUpcomingServices = React.useMemo(() => {
        if (!current?.vehicleId) return false;
        if (maintenanceState.vehicleId !== current.vehicleId) return false;

        return (
            (maintenanceState.items ?? [])
                .filter((m) => m.vehicleId === current.vehicleId)
                .filter(isServiceVisibleForDriver).length > 0
        );
    }, [
        current?.vehicleId,
        maintenanceState.items,
        maintenanceState.vehicleId,
    ]);

    const handleLogoutClick = () => {
        void dispatch(handleLogout(() => navigate("/login")));
    };

    const toggleMenuOpen = () => {
        setMenuOpen(!menuOpen);
    };

    const goBack = () => {
        if (!hasUnconfirmedNotifications) {
            if (location.pathname.startsWith("/notifications")) {
                navigate("/");
            } else {
                navigate(-1);
            }
        }
    };

    const navItems: INavItemProps[] = [
        {
            path: "/",
            label: t("header.home"),
            icon: <HomeIcon />,
        },
        {
            path: "/services",
            label: t("maintenance.title"),
            icon: <BuildIcon />,
            isVisible: () => telematicsEnabled && hasUpcomingServices,
            hasBadgeDot: hasUpcomingServices,
        },
        {
            path: "/worklog",
            label: t("workTime.workLog"),
            icon: <AccessTimeIcon />,
        },
        {
            path: "/worklog/calendar",
            label: t("workTime.workTimeLog"),
            icon: <CalendarTodayIcon />,
        },
        {
            path: "/work/form/new",
            label: t("workTime.newWorkTimeEntry"),
            icon: <MoreTimeIcon />,
        },
        {
            path: "/work/form/daybooking/new",
            label: t("dayBooking.newDayBooking"),
            icon: <EventIcon />,
            isVisible: () => canAddAndEditDayBookings,
        },
        {
            path: "/forms",
            label: t("forms.title"),
            icon: <Assignment />,
            isVisible: () => formsEnabled,
        },
        {
            path: "/transport/orders",
            label: t("transport.orders.header.title"),
            icon: <Inventory />,
            isVisible: () => transportEnabled,
        },
        {
            path: "/notifications",
            label: t("notifications.notifications"),
            icon: <Notifications />,
            isVisible: () => orderNotificationsEnabled,
        },
        {
            path: "/settings",
            label: t("settings.settings"),
            icon: <SettingsIcon />,
        },
        {
            label: t("header.logout"),
            icon: <LogoutIcon />,
            onClick: handleLogoutClick,
        },
    ];

    const isHomePage = !!matchPath(location.pathname, "/");

    return (
        <>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static" sx={{ bgcolor: "primary.dark" }}>
                    <Toolbar>
                        {!isHomePage && !hasUnconfirmedNotifications ? (
                            <Button
                                color="white"
                                sx={{
                                    textTransform: "capitalize",
                                    width: 64,
                                }}
                                onClick={goBack}
                            >
                                <ArrowBackIcon color="success" sx={{ mr: 1 }} />
                                {t("header.back")}
                            </Button>
                        ) : null}
                        <Box ml={4} />
                        <HeaderLogo
                            sx={{
                                margin: "0 auto",
                                maxWidth: hasUnconfirmedNotifications
                                    ? "300px"
                                    : "100%",
                            }}
                        />
                        {!hasUnconfirmedNotifications ? (
                            <Box ml={!isHomePage ? "64px" : undefined}>
                                <IconButton
                                    size="large"
                                    edge="start"
                                    color="inherit"
                                    aria-label="menu"
                                    onClick={toggleMenuOpen}
                                >
                                    {hasUpcomingServices ? (
                                        <Badge
                                            variant="dot"
                                            color="info"
                                            overlap="circular"
                                            sx={{
                                                "& .MuiBadge-badge": {
                                                    bgcolor: "#1976d2",
                                                },
                                            }}
                                        >
                                            <MenuIcon />
                                        </Badge>
                                    ) : (
                                        <MenuIcon />
                                    )}
                                </IconButton>
                            </Box>
                        ) : null}
                    </Toolbar>
                </AppBar>
            </Box>
            {!hasUnconfirmedNotifications ? (
                <SwipeableDrawer
                    anchor="top"
                    open={menuOpen}
                    onClose={toggleMenuOpen}
                    onOpen={toggleMenuOpen}
                >
                    <Box
                        sx={{
                            width: "auto",
                            bgcolor: "primary.dark",
                            color: "white.main",
                        }}
                        role="presentation"
                        onClick={toggleMenuOpen}
                        onKeyDown={toggleMenuOpen}
                    >
                        <Toolbar sx={{ pl: "12px !important" }}>
                            <HeaderLogo sx={{ backgroundPosition: "left" }} />
                            <Box>
                                <IconButton
                                    size="large"
                                    edge="start"
                                    color="inherit"
                                    aria-label="menu"
                                    onClick={toggleMenuOpen}
                                >
                                    {hasUpcomingServices ? (
                                        <Badge
                                            variant="dot"
                                            color="info"
                                            overlap="circular"
                                            sx={{
                                                "& .MuiBadge-badge": {
                                                    bgcolor: "#1976d2",
                                                },
                                            }}
                                        >
                                            <MenuIcon />
                                        </Badge>
                                    ) : (
                                        <MenuIcon />
                                    )}
                                </IconButton>
                            </Box>
                        </Toolbar>
                        <List>
                            {navItems.map((item) => (
                                <NavItem key={item.label} {...item} />
                            ))}
                        </List>
                    </Box>
                </SwipeableDrawer>
            ) : null}
        </>
    );
};
